import { RadioGroupField } from '@monorepo/shared/componentsV2/fields/RadioGroupField';
import { TimePickerField } from '@monorepo/shared/componentsV2/fields/TimePickerField';
import { StyledInput } from '@monorepo/shared/componentsV2/styled/form';
import { SelectableOption } from '@monorepo/shared/types/SelectableOption';
import {
  BooleanValue,
  DatetimeValue,
  DateValue,
  FieldDataType,
  FieldResponse,
  NullableBoolean,
  NumericFieldResponse,
  NumericValue,
  ResourceFieldResponse,
  ResourceValue,
  SaveFieldValueRequest,
  SingleSelectFieldResponse,
  SingleSelectValue,
  SpecificFieldValue,
  TextValue,
  TimeValue,
  UserValue,
} from 'mapistry-shared';
import React from 'react';
import { EditCompactBooleanFieldValue } from './EditCompactBooleanFieldValue';
import {
  EditDateLikeValueFactory,
  getDateLikeFieldValueFromForm,
} from './EditDateLikeValueFactory';
import {
  EditNumericFieldValue,
  getNumericFieldValueFromForm,
} from './EditNumericFieldValue';
import {
  EditResourceFieldValue,
  getResourceFieldValueFromForm,
} from './EditResourceFieldValue';
import { EditSingleSelectFieldValue } from './EditSingleSelectFieldValue';
import {
  EditUserFieldValue,
  getUserFieldValueFromForm,
} from './EditUserFieldValue';
import { FormValues, isNullableString } from './types';
import {
  boolFieldValueToFormInitialValue,
  getUnexpectedTypeErrorAndLogToSentry,
  getValidateFunction,
  stringFieldValueToFormInitialValue,
} from './utils';

type EditFieldValueFactoryProps = {
  className?: string;
  field: FieldResponse;
  fieldValue?: SpecificFieldValue;
  isRequiredOverride?: boolean;
  labelOverride?: string;
  organizationId: string;
  nameOverride?: string;
  projectId: string | undefined;
  isReadOnly?: boolean;
  isLabelHidden?: boolean;
  isCompact?: boolean;
  isErrorTextHidden?: boolean;
  isSubtextHidden?: boolean;
  forceShowErrors?: boolean;
};

const booleanRadioOptions: SelectableOption[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

export function EditFieldValueFactory({
  className,
  field,
  fieldValue,
  isRequiredOverride,
  labelOverride,
  nameOverride,
  organizationId,
  projectId,
  isReadOnly,
  isLabelHidden,
  isCompact,
  isErrorTextHidden,
  isSubtextHidden,
  forceShowErrors,
}: EditFieldValueFactoryProps) {
  const label = labelOverride == null ? field.name : labelOverride;
  const isRequired =
    isRequiredOverride == null ? field.isRequired : isRequiredOverride;
  const validate = getValidateFunction(isRequired);
  const name = nameOverride == null ? field.id : nameOverride;

  switch (field.type) {
    case FieldDataType.TEXT: {
      const initialValue = stringFieldValueToFormInitialValue(
        fieldValue as TextValue | undefined,
      );
      return (
        <StyledInput
          className={className}
          subText={field.description}
          label={label}
          name={name}
          initialValue={initialValue}
          validate={validate}
          required={isRequired}
          disabled={isReadOnly}
          isLabelHidden={isLabelHidden}
          isCompact={isCompact}
          isErrorTextHidden={isErrorTextHidden}
          isSubtextHidden={isSubtextHidden}
          forceShowErrors={forceShowErrors}
        />
      );
    }
    case FieldDataType.NUMERIC:
      return (
        <EditNumericFieldValue
          className={className}
          field={field as NumericFieldResponse}
          fieldValue={fieldValue as NumericValue | undefined}
          subText={field.description}
          isRequired={isRequired}
          label={label}
          name={name}
          disabled={isReadOnly}
          isReadOnly={isReadOnly}
          isLabelHidden={isLabelHidden}
          isCompact={isCompact}
          isErrorTextHidden={isErrorTextHidden}
          isSubtextHidden={isSubtextHidden}
          forceShowErrors={forceShowErrors}
        />
      );
    case FieldDataType.BOOLEAN: {
      const initialValue = boolFieldValueToFormInitialValue(
        fieldValue as BooleanValue | undefined,
      );
      if (isCompact) {
        return (
          <EditCompactBooleanFieldValue
            className={className}
            initialValue={initialValue}
            label={label}
            name={name}
            required={isRequired}
            isLabelHidden
            isCompact={isCompact}
            isErrorTextHidden={isErrorTextHidden}
            isSubtextHidden={isSubtextHidden}
          />
        );
      }
      return (
        <RadioGroupField
          className={className}
          subText={field.description}
          initialValue={initialValue}
          label={label}
          name={name}
          options={booleanRadioOptions}
          required={isRequired}
          row
          validate={validate}
          isReadOnly={isReadOnly}
          isLabelHidden={isLabelHidden}
          isErrorTextHidden={isErrorTextHidden}
          isSubtextHidden={isSubtextHidden}
          forceShowErrors={forceShowErrors}
        />
      );
    }
    case FieldDataType.SINGLE_SELECT: {
      return (
        <EditSingleSelectFieldValue
          className={className}
          field={field as SingleSelectFieldResponse}
          fieldValue={fieldValue as SingleSelectValue | undefined}
          subText={field.description}
          isRequired={isRequired}
          label={label}
          name={name}
          isReadOnly={isReadOnly}
          isLabelHidden={isLabelHidden}
          isCompact={isCompact}
          isErrorTextHidden={isErrorTextHidden}
          isSubtextHidden={isSubtextHidden}
          forceShowErrors={forceShowErrors}
        />
      );
    }
    case FieldDataType.DATETIME:
    case FieldDataType.DATE: {
      return (
        <EditDateLikeValueFactory
          className={className}
          field={field}
          fieldValue={fieldValue as DateValue | DatetimeValue | undefined}
          subText={field.description}
          isRequired={isRequired}
          label={label}
          name={name}
          disabled={isReadOnly}
          isLabelHidden={isLabelHidden}
          isCompact={isCompact}
          isErrorTextHidden={isErrorTextHidden}
          isSubtextHidden={isSubtextHidden}
          forceShowErrors={forceShowErrors}
        />
      );
    }
    case FieldDataType.TIME: {
      const initialValue = stringFieldValueToFormInitialValue(
        fieldValue as TimeValue | undefined,
      );
      return (
        <TimePickerField
          className={className}
          subText={field.description}
          label={label}
          name={name}
          initialValue={initialValue}
          validate={validate}
          required={isRequired}
          disabled={isReadOnly}
          isLabelHidden={isLabelHidden}
          isCompact={isCompact}
          isErrorTextHidden={isErrorTextHidden}
          isSubtextHidden={isSubtextHidden}
          forceShowErrors={forceShowErrors}
        />
      );
    }
    case FieldDataType.RESOURCE:
      return (
        <EditResourceFieldValue
          className={className}
          field={field as ResourceFieldResponse}
          fieldValue={fieldValue as ResourceValue | undefined}
          subText={field.description}
          isRequired={isRequired}
          label={label}
          name={name}
          organizationId={organizationId}
          projectId={projectId}
          isReadOnly={isReadOnly}
          isLabelHidden={isLabelHidden}
          isCompact={isCompact}
          isErrorTextHidden={isErrorTextHidden}
          isSubtextHidden={isSubtextHidden}
          forceShowErrors={forceShowErrors}
        />
      );
    case FieldDataType.USER:
      if (!projectId) {
        throw new Error('Cannot have user field without project context');
      }
      return (
        <EditUserFieldValue
          className={className}
          fieldValue={fieldValue as UserValue | undefined}
          subText={field.description}
          isRequired={isRequired}
          label={label}
          name={name}
          organizationId={organizationId}
          projectId={projectId}
          isReadOnly={isReadOnly}
          isLabelHidden={isLabelHidden}
          isCompact={isCompact}
          isErrorTextHidden={isErrorTextHidden}
          isSubtextHidden={isSubtextHidden}
          forceShowErrors={forceShowErrors}
        />
      );
    default: {
      const exhaustiveCheck: never | FieldDataType.FORMULA = field.type;
      throw new Error(`Unimplemented field data type - ${exhaustiveCheck}`);
    }
  }
}

export function getFieldValueFromForm(
  field: FieldResponse,
  formValues: FormValues,
): SaveFieldValueRequest {
  switch (field.type) {
    case FieldDataType.SINGLE_SELECT:
    case FieldDataType.TEXT:
    case FieldDataType.TIME: {
      const value = formValues[field.id] || null;
      if (!isNullableString(value)) {
        throw getUnexpectedTypeErrorAndLogToSentry(field, value);
      }
      return {
        fieldId: field.id,
        fieldValue: { value },
      };
    }
    case FieldDataType.BOOLEAN: {
      const formValue = formValues[field.id];
      let parsedValue: NullableBoolean;
      switch (formValue) {
        case 'true':
          parsedValue = true;
          break;
        case 'false':
          parsedValue = false;
          break;
        case null:
        case undefined:
          parsedValue = null;
          break;
        default:
          throw getUnexpectedTypeErrorAndLogToSentry(field, formValue);
      }
      return {
        fieldId: field.id,
        fieldValue: { value: parsedValue },
      };
    }
    case FieldDataType.DATETIME:
    case FieldDataType.DATE: {
      return getDateLikeFieldValueFromForm(field, formValues);
    }
    case FieldDataType.NUMERIC:
      return getNumericFieldValueFromForm(
        field as NumericFieldResponse,
        formValues,
      );
    case FieldDataType.RESOURCE:
      return getResourceFieldValueFromForm(
        field as ResourceFieldResponse,
        formValues,
      );
    case FieldDataType.USER:
      return getUserFieldValueFromForm(field, formValues);
    default: {
      const exhaustiveCheck: never | FieldDataType.FORMULA = field.type;
      throw new Error(`Unhandled field data type - ${exhaustiveCheck}`);
    }
  }
}
